import React, { useState } from 'react';
import { HiX, HiChevronLeft, HiChevronRight } from 'react-icons/hi';
import useScrollReveal from '../hooks/useScrollReveal';

const plotImages = [
  { src: '/assets/Plots/Plot-1.jpeg', alt: 'Plot 1' },
  { src: '/assets/Plots/Plot-2.jpeg', alt: 'Plot 2' },
  { src: '/assets/Plots/Plot-3.jpeg', alt: 'Plot 3' },
  { src: '/assets/Plots/Plot-4.jpeg', alt: 'Plot 4' },
  { src: '/assets/Plots/Plot-5.jpeg', alt: 'Plot 5' },
  { src: '/assets/Plots/Plot-6.jpeg', alt: 'Plot 6' },
  { src: '/assets/Plots/Plot-7.jpeg', alt: 'Plot 7' },
  { src: '/assets/Plots/Plot-8.jpeg', alt: 'Plot 8' },
  { src: '/assets/Plots/Plot-9.jpeg', alt: 'Plot 9' },
];

const clientImages = [
  { src: '/assets/Clients/PrimeAcres-Client-1.jpeg', alt: 'Client 1' },
  { src: '/assets/Clients/PrimeAcres-Client-2.jpeg', alt: 'Client 2' },
  { src: '/assets/Clients/PrimeAcres-Client-3.jpeg', alt: 'Client 3' },
  { src: '/assets/Clients/PrimeAcres-Client-4.jpeg', alt: 'Client 4' },
  { src: '/assets/Clients/PrimeAcres-Client-5.jpeg', alt: 'Client 5' },
  { src: '/assets/Clients/PrimeAcres-Client-6.jpeg', alt: 'Client 6' },
  { src: '/assets/Clients/PrimeAcres-Client-7.jpeg', alt: 'Client 7' },
  { src: '/assets/Clients/PrimeAcres-Client-8.jpeg', alt: 'Client 8' },
  { src: '/assets/Clients/PrimeAcres-Client-9.jpeg', alt: 'Client 9' },
  { src: '/assets/Clients/PrimeAcres-Client-10.jpeg', alt: 'Client 10' },
  { src: '/assets/Clients/PrimeAcres-Client-11.jpeg', alt: 'Client 11' },
  { src: '/assets/Clients/PrimeAcres-Client-12.jpeg', alt: 'Client 12' },
  { src: '/assets/Clients/PrimeAcres-Client-13.jpeg', alt: 'Client 13' },
];

const GalleryGrid = ({ images, gridVisible, onOpen }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 auto-rows-[180px] sm:auto-rows-[200px]">
    {images.map((img, i) => (
      <div
        key={img.src}
        className={`relative overflow-hidden rounded-2xl cursor-pointer group transition-all duration-700 ${
          gridVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
        }`}
        style={{ transitionDelay: gridVisible ? `${i * 60}ms` : '0ms' }}
        onClick={() => onOpen(i)}
      >
        <img
          src={img.src}
          alt={img.alt}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-[1500ms]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/0 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500" />
      </div>
    ))}
  </div>
);

const allImages = [...plotImages, ...clientImages];

const tabs = [
  { label: 'All', images: allImages },
  { label: 'Plots', images: plotImages },
  { label: 'Our Clients', images: clientImages },
];

const Gallery = () => {
  const [lightbox, setLightbox] = useState(null);
  const [activeTab, setActiveTab] = useState('All');
  const [headRef, headVisible] = useScrollReveal();
  const [gridRef, gridVisible] = useScrollReveal({ threshold: 0.1 });

  const activeImages = tabs.find((t) => t.label === activeTab)?.images || [];

  const openLightbox = (index) => setLightbox({ images: activeImages, index });
  const closeLightbox = () => setLightbox(null);

  const navigateLightbox = (dir) => {
    setLightbox((prev) => {
      const next = prev.index + dir;
      if (next < 0) return { ...prev, index: prev.images.length - 1 };
      if (next >= prev.images.length) return { ...prev, index: 0 };
      return { ...prev, index: next };
    });
  };

  return (
    <section id="gallery" className="py-24 lg:py-32 relative bg-gray-100 border-t border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header + Tab Filter */}
        <div ref={headRef} className={`flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12 transition-all duration-700 ${headVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="max-w-lg">
            <span className="inline-block text-[11px] tracking-[0.3em] uppercase font-semibold text-accent-500 mb-5">
              Visual Tour
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading text-dark-800 leading-tight">
              Gallery
            </h2>
          </div>
          {/* Tab Buttons */}
          <div className="flex gap-2 flex-wrap">
            {tabs.map((tab) => (
              <button
                key={tab.label}
                onClick={() => setActiveTab(tab.label)}
                className={`px-4 py-2 rounded-full text-[11px] tracking-[0.12em] uppercase font-medium transition-all duration-500 ${
                  activeTab === tab.label
                    ? 'bg-primary-600 text-white shadow-sm'
                    : 'bg-white text-dark-400 hover:text-primary-600 border border-gray-200 hover:border-primary-200'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Gallery Grid */}
        <div ref={gridRef}>
          <GalleryGrid images={activeImages} gridVisible={gridVisible} onOpen={openLightbox} />
        </div>

        {/* Download Brochure */}
        <div className="text-center mt-12">
          <a
            href="/Brochure.pdf"
            download
            className="inline-flex items-center gap-2 border-2 border-dark-700 text-dark-700 rounded-full px-7 py-3 text-[11px] font-semibold tracking-wider uppercase hover:bg-dark-700 hover:text-white transition-all duration-500"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Download Brochure
          </a>
        </div>
      </div>

      {lightbox !== null && (
        <div
          className="fixed inset-0 z-50 bg-black/95 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          <button
            className="absolute top-6 right-6 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-all z-10"
            onClick={closeLightbox}
          >
            <HiX className="text-xl" />
          </button>

          <div className="absolute top-7 left-6 text-white/50 text-sm font-light z-10">
            <span className="text-white font-heading text-lg">{String(lightbox.index + 1).padStart(2, '0')}</span>
            <span className="mx-2">/</span>
            <span>{String(lightbox.images.length).padStart(2, '0')}</span>
          </div>

          <button
            className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/5 hover:bg-white/15 backdrop-blur-sm border border-white/10 rounded-full flex items-center justify-center text-white transition-all z-10"
            onClick={(e) => { e.stopPropagation(); navigateLightbox(-1); }}
          >
            <HiChevronLeft className="text-xl" />
          </button>
          <button
            className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/5 hover:bg-white/15 backdrop-blur-sm border border-white/10 rounded-full flex items-center justify-center text-white transition-all z-10"
            onClick={(e) => { e.stopPropagation(); navigateLightbox(1); }}
          >
            <HiChevronRight className="text-xl" />
          </button>

          <img
            src={lightbox.images[lightbox.index]?.src}
            alt={lightbox.images[lightbox.index]?.alt}
            className="max-w-full max-h-[85vh] object-contain rounded-2xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </section>
  );
};

export default Gallery;
